import React, { useState, useEffect, useMemo, useRef } from "react";
import { apiFetch } from "../utils/api";
import { showError, showToast } from "../utils/notifications";
import {
  formatAppDate,
  formatAppIntlLabel,
  formatAppTime,
  parseDbDateTime,
} from "../utils/appDateTime";

function parseShiftInstant(v) {
  return parseDbDateTime(v);
}

export function buildUnassignAvailabilityOptions(shiftStartAt, shiftEndAt) {
  const start = parseShiftInstant(shiftStartAt);
  const end = parseShiftInstant(shiftEndAt);
  const fallback = [
    { key: "available", label: "Available during shift" },
    { key: "unavailable_shift", label: "Unavailable during shift" },
    { key: "unavailable_day", label: "Unavailable all day" },
    { key: "unavailable_range", label: "Unavailable date range" },
  ];
  if (!start) return fallback;
  const dayShort = formatAppIntlLabel(start, { month: "short", day: "numeric" });
  const dayLong = formatAppDate(start);
  const t1 = formatAppTime(start).toLowerCase();
  const t2 = end ? formatAppTime(end).toLowerCase() : t1;
  return [
    {
      key: "available",
      label: `Available ${dayShort} · ${t1} – ${t2}`,
    },
    {
      key: "unavailable_shift",
      label: `Unavailable ${dayShort} · ${t1} – ${t2}`,
    },
    {
      key: "unavailable_day",
      label: `Unavailable all day ${dayLong}`,
    },
    { key: "unavailable_range", label: "Unavailable date range" },
  ];
}

/**
 * Full "Unassign Shift" form + POST /api/shifts/:id/force-unassign (Schedule, Jobs kebab, etc.).
 */
export default function UnassignShiftDialog({
  open,
  onClose,
  shiftId,
  assignedAccountId,
  staffDisplayName = "",
  shiftStartAt = null,
  shiftEndAt = null,
  onSuccess,
  initialValues = null,
  onSubmitOverride = null,
  title = "Unassign Shift",
  successMessage = "Staff unassigned from shift",
  /** When true, unassign still runs but no toast (e.g. replace flow — assign step shows one success). */
  suppressSuccess = false,
}) {
  const [submitting, setSubmitting] = useState(false);
  const [countTowardsReliability, setCountTowardsReliability] = useState(true);
  const [reason, setReason] = useState("Other");
  const [notice24h, setNotice24h] = useState("yes");
  const [availability, setAvailability] = useState("available");
  const [note, setNote] = useState("");

  const snapshotRef = useRef({});
  snapshotRef.current = {
    countTowardsReliability,
    reason,
    notice24h,
    availability,
    note,
  };

  useEffect(() => {
    if (!open) return;
    setSubmitting(false);
    setCountTowardsReliability(
      initialValues?.countTowardsReliability ??
        initialValues?.affectsReliability ??
        true,
    );
    setReason(initialValues?.reason || "Other");
    setNotice24h(initialValues?.notice24h || "yes");
    setAvailability(initialValues?.availability || "available");
    setNote(initialValues?.note || "");
  }, [open, initialValues]);

  const availabilityOptions = useMemo(
    () => buildUnassignAvailabilityOptions(shiftStartAt, shiftEndAt),
    [shiftStartAt, shiftEndAt],
  );

  const handleSubmit = async () => {
    if (submitting) return;
    if (!onSubmitOverride && (!shiftId || !assignedAccountId)) return;
    const m = snapshotRef.current;
    setSubmitting(true);
    try {
      if (typeof onSubmitOverride === "function") {
        await Promise.resolve(onSubmitOverride(m));
        if (typeof onSuccess === "function") await Promise.resolve(onSuccess());
        onClose();
        return;
      }

      const reasonParts = [];
      if (m.reason) reasonParts.push(`Reason: ${m.reason}`);
      if (m.notice24h)
        reasonParts.push(
          `24h notice: ${m.notice24h === "yes" ? "Yes" : "No"}`,
        );
      if (m.availability) {
        const map = {
          available: "Available during shift",
          unavailable_shift: "Unavailable during shift",
          unavailable_day: "Unavailable all day",
          unavailable_range: "Unavailable date range",
        };
        reasonParts.push(
          `Availability: ${map[m.availability] || m.availability}`,
        );
      }
      if (m.note && m.note.trim()) reasonParts.push(`Note: ${m.note.trim()}`);
      const reasonStr = reasonParts.join(" | ");

      const reasonCode =
        m.reason === "Sick"
          ? "sick"
          : m.reason === "MIA/No Show"
            ? "mia"
            : "other";

      const resp = await apiFetch(`/api/shifts/${shiftId}/force-unassign`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          accountId: Number(assignedAccountId),
          reason: reasonStr,
          reasonCode,
          note: m.note && m.note.trim() ? m.note.trim() : null,
          notice24h: m.notice24h === "yes" ? 1 : 0,
          affectsReliability: Boolean(m.countTowardsReliability),
        }),
      });
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok || !json.success) {
        throw new Error(json.message || "Failed to unassign staff");
      }
      if (!suppressSuccess) {
        showToast(successMessage);
      }
      if (typeof onSuccess === "function") await Promise.resolve(onSuccess());
      onClose();
    } catch (err) {
      console.error(err);
      showError(err.message || "Failed to unassign staff");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-40 z-[50300] flex items-center justify-center p-4"
      onClick={() => {
        if (!submitting) onClose();
      }}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-semibold mb-1">{title}</h3>
        {staffDisplayName ? (
          <p className="text-sm text-gray-600 mb-4">{staffDisplayName}</p>
        ) : (
          <div className="mb-4" />
        )}

        <div className="space-y-4 text-sm">
          <div>
            <div className="text-gray-600 mb-2">Count towards reliability</div>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  checked={Boolean(countTowardsReliability)}
                  onChange={() => setCountTowardsReliability(true)}
                  disabled={submitting}
                />
                Yes
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  checked={!countTowardsReliability}
                  onChange={() => setCountTowardsReliability(false)}
                  disabled={submitting}
                />
                No
              </label>
            </div>
          </div>

          <div>
            <div className="text-gray-600 mb-2">Reason</div>
            <div className="flex items-center gap-6 flex-wrap">
              {["Sick", "MIA/No Show", "Other"].map((r) => (
                <label key={r} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={reason === r}
                    onChange={() => setReason(r)}
                    disabled={submitting}
                  />
                  {r}
                </label>
              ))}
            </div>
          </div>

          <div>
            <div className="text-gray-600 mb-2">24 hour notice</div>
            <div className="flex flex-col gap-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  checked={notice24h === "yes"}
                  onChange={() => setNotice24h("yes")}
                  disabled={submitting}
                />
                Yes, notification more than 24 hours
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  checked={notice24h === "no"}
                  onChange={() => setNotice24h("no")}
                  disabled={submitting}
                />
                No, notification less than 24 hours
              </label>
            </div>
          </div>

          <div>
            <div className="text-gray-600 mb-2">Availability</div>
            <div className="space-y-2">
              {availabilityOptions.map((opt) => (
                <label
                  key={opt.key}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <input
                    type="radio"
                    checked={availability === opt.key}
                    onChange={() => setAvailability(opt.key)}
                    disabled={submitting}
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>

          <div>
            <div className="text-gray-600 mb-2">Note</div>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              disabled={submitting}
              className="w-full border-b border-gray-300 focus:outline-none focus:border-red-500 py-1"
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button
            type="button"
            className="px-4 py-2 text-sm border rounded hover:bg-gray-100"
            onClick={() => !submitting && onClose()}
            disabled={submitting}
          >
            CANCEL
          </button>
          <button
            type="button"
            className="px-4 py-2 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-60"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting
              ? onSubmitOverride
                ? "SAVING..."
                : "UNASSIGNING..."
              : onSubmitOverride
                ? "SAVE"
                : countTowardsReliability
                  ? "UNASSIGN & REDUCE RELIABILITY"
                  : "UNASSIGN"}
          </button>
        </div>
      </div>
    </div>
  );
}
